import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID } from '../../../contracts/heidi-2';
import { WORK_CHAT_HOME_PATH } from '../../../contracts/projects/chat-home-path';
import {
  computeIsHeidi2EvidenceTourEligible,
  getHeidi2Ports,
  heidi2EvidenceTourAddMenuMountCountAtom,
  heidi2EvidenceTourHeadingHomeAtom,
  heidi2EvidenceTourRailMountedAtom,
  heidi2EvidenceTourStepAtom,
  heidi2ExploreLaterRestartAtom,
  HEIDI_2_EVIDENCE_TOUR_TOTAL_STEPS,
  useHeidi2ExploreLaterState,
} from '../../../data/heidi-2';

import {
  messages,
  trackTourDismiss,
  useIsHeidi2EvidenceTourOnRecents,
  useTrackTourStepViewed,
} from './heidi-2-evidence-tour-shared';
import { Heidi2SpotlightCard } from './heidi-2-spotlight-card';
const progressLabel = (current: number) => (
  <FormattedMessage
    {...messages.progress}
    values={{ current, total: HEIDI_2_EVIDENCE_TOUR_TOTAL_STEPS }}
  />
);

interface Heidi2EvidenceTourHomeStepProps {
  children: ReactNode;
  /** Takes the clinician to Home, the way the Home nav item does. */
  onShowMe: () => void;
}

/**
 * Step 0, anchored to the rail's Home item, shown on any page with the rail.
 * "Next" opens Home and advances once Home is reached. Reads billing through the
 * explore-later state, so mount it on cloud builds only.
 */
export const Heidi2EvidenceTourHomeStep = (props: Heidi2EvidenceTourHomeStepProps) => (
  <Heidi2EvidenceTourHomeStepContent
    key={useAtomValue(heidi2ExploreLaterRestartAtom)}
    {...props}
  />
);

const Heidi2EvidenceTourHomeStepContent = ({
  children,
  onShowMe,
}: Heidi2EvidenceTourHomeStepProps) => {
  const intl = useIntl();
  const ports = getHeidi2Ports();
  const { isOpen, closeWithOptions } = ports.useQueuedModal(
    HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID
  );
  const [step, setStep] = useAtom(heidi2EvidenceTourStepAtom);
  const [isHeadingHome, setIsHeadingHome] = useAtom(heidi2EvidenceTourHeadingHomeAtom);
  const openHomePanel = ports.useOpenHomePanel();
  const state = useHeidi2ExploreLaterState();
  const isTourPending = computeIsHeidi2EvidenceTourEligible(state);
  const isOnWorkHome = ports.usePathname().startsWith(WORK_CHAT_HOME_PATH);
  const setRailMounted = useSetAtom(heidi2EvidenceTourRailMountedAtom);
  // "Next" hides the card straight away but leaves the step on the rail until
  // Home is reached, so the tour keeps its queue slot for the walk.
  const isShowing = isOpen && step === 0 && !isHeadingHome;

  useEffect(() => {
    setRailMounted(true);
    return () => setRailMounted(false);
  }, [setRailMounted]);

  useTrackTourStepViewed(isShowing, 0);

  useEffect(() => {
    if (!isTourPending || step !== 0 || !isHeadingHome || !isOnWorkHome) return;
    setIsHeadingHome(false);
    setStep(1);
  }, [isHeadingHome, isOnWorkHome, isTourPending, setIsHeadingHome, setStep, step]);

  if (!isShowing) return <>{children}</>;

  return (
    <Heidi2SpotlightCard
      testId="heidi-2-evidence-tour-home"
      targetSelector="[data-heidi-2-spotlight-target]"
      title={<FormattedMessage {...messages.homeTitle} />}
      description={<FormattedMessage {...messages.homeDescription} />}
      progress={progressLabel(1)}
      actionLabel={<FormattedMessage {...messages.next} />}
      onAction={() => {
        ports.trackButtonClicked({
          screen_name: ScreenName.Heidi2EvidenceTourSpotlight,
          action: ButtonAction.Heidi2EvidenceTourShowMe,
          label: intl.formatMessage(messages.next),
        });
        setIsHeadingHome(true);
        openHomePanel();
        onShowMe();
      }}
      onClose={() => {
        trackTourDismiss(0);
        closeWithOptions({ action: ModalViewAction.dismiss });
      }}
      // The item navigates Home itself; follow it as "Next" would.
      onItemPointerDown={() => {
        setIsHeadingHome(true);
        openHomePanel();
      }}
    >
      {children}
    </Heidi2SpotlightCard>
  );
};

/** Step 1, anchored to Recents in the Home panel. "Next" opens the + menu step. */
export const Heidi2EvidenceTourRecentsStep = ({ children }: { children: ReactNode }) => {
  const intl = useIntl();
  const ports = getHeidi2Ports();
  const { closeWithOptions } = ports.useQueuedModal(
    HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID
  );
  const setStep = useSetAtom(heidi2EvidenceTourStepAtom);
  const isShowing = useIsHeidi2EvidenceTourOnRecents();

  useTrackTourStepViewed(isShowing, 1);

  if (!isShowing) return <>{children}</>;

  return (
    <Heidi2SpotlightCard
      testId="heidi-2-evidence-tour-recents"
      title={<FormattedMessage {...messages.recentsTitle} />}
      description={<FormattedMessage {...messages.recentsDescription} />}
      progress={progressLabel(2)}
      actionLabel={<FormattedMessage {...messages.next} />}
      onAction={() => {
        ports.trackButtonClicked({
          screen_name: ScreenName.Heidi2EvidenceTourSpotlight,
          action: ButtonAction.Heidi2EvidenceTourShowMe,
          label: intl.formatMessage(messages.next),
        });
        setStep(2);
      }}
      onClose={() => {
        trackTourDismiss(1);
        closeWithOptions({ action: ModalViewAction.dismiss });
      }}
    >
      {children}
    </Heidi2SpotlightCard>
  );
};

/** The + menu itself; the card already treats its portaled flyouts as inside. */
const ADD_MENU_SELECTOR = '[data-add-menu]';

export interface Heidi2EvidenceTourAddMenuSpotlight {
  open: boolean;
  wrap: (row: ReactNode) => ReactNode;
  /** Engage the tour when the clinician toggles Evidence-only during this step. */
  onModeRowToggle?: () => void;
}

/**
 * Step 2, anchored to the Evidence-only row in the Work home composer + menu.
 * Render-prop so LinkContextButton can hold the menu open and wrap that row
 * without importing the tour.
 */
export const Heidi2EvidenceTourAddMenuStep = ({
  children,
}: {
  children: (spotlight: Heidi2EvidenceTourAddMenuSpotlight) => ReactNode;
}) => {
  const intl = useIntl();
  const ports = getHeidi2Ports();
  const { isOpen, closeWithOptions } = ports.useQueuedModal(
    HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID
  );
  const step = useAtomValue(heidi2EvidenceTourStepAtom);
  const setHeadingHome = useSetAtom(heidi2EvidenceTourHeadingHomeAtom);
  const setAddMenuMountCount = useSetAtom(heidi2EvidenceTourAddMenuMountCountAtom);
  const isShowing = isOpen && step === 2;

  useEffect(() => {
    setAddMenuMountCount((count) => count + 1);
    return () => setAddMenuMountCount((count) => count - 1);
  }, [setAddMenuMountCount]);

  useTrackTourStepViewed(isShowing, 2);

  // A dismiss mid-walk (or a prior walk) may have left this set; clear it once
  // we land on the final step so a later restart does not skip the rail.
  useEffect(() => {
    if (!isShowing) return;
    setHeadingHome(false);
  }, [isShowing, setHeadingHome]);

  if (!isShowing) {
    return <>{children({ open: false, wrap: (row) => row })}</>;
  }

  const engageTour = () => closeWithOptions({ action: ModalViewAction.engage });

  // Radix Anchor is not exempt from outside dismiss — clicks on the + menu
  // (or its Evidence-only row) land outside SpotlightContent and would close
  // the tour as a dismiss. Keep the tour up for anything inside the menu; the
  // card covers its portaled flyouts (Routines, Speed, Sources, Shortcuts).
  const keepOpenForAddMenu = (event: Event) => {
    const target = event.target;
    if (target instanceof Element && target.closest(ADD_MENU_SELECTOR) != null) {
      event.preventDefault();
    }
  };

  return (
    <>
      {children({
        open: true,
        onModeRowToggle: engageTour,
        wrap: (row) => (
          <Heidi2SpotlightCard
            testId="heidi-2-evidence-tour-add-menu"
            title={<FormattedMessage {...messages.addMenuTitle} />}
            description={<FormattedMessage {...messages.addMenuDescription} />}
            progress={progressLabel(3)}
            actionLabel={<FormattedMessage {...messages.gotIt} />}
            onAction={() => {
              ports.trackButtonClicked({
                screen_name: ScreenName.Heidi2EvidenceTourSpotlight,
                action: ButtonAction.Heidi2EvidenceTourGotIt,
                label: intl.formatMessage(messages.gotIt),
              });
              engageTour();
            }}
            onClose={() => {
              trackTourDismiss(2);
              closeWithOptions({ action: ModalViewAction.dismiss });
            }}
            onInteractOutside={keepOpenForAddMenu}
          >
            {row}
          </Heidi2SpotlightCard>
        ),
      })}
    </>
  );
};
